import React, { Fragment, PureComponent } from 'react';
import NationalityAutocomplete from '~/AeMaxPrice/NationalityAutocomplete/NationalityAutocompleteContainer';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { formatNumericHumanDate } from '~/DateTime';
import { DebounceInput } from '~/Debounce';
import Icon from '~/UI/Icon/Icon';
import Input from '~/UI/Input/Input';
import SaveActivityIndicator, { Status } from '~/UI/SaveActivityIndicator/SaveActivityIndicator';
import Select from '~/UI/Select/Select';
import Tooltip from '~/UI/Tooltip/Tooltip';

import styles from './styles.less';

const TextInputStyles = {
	label: styles.parameterLabel,
	input: styles.parameterInput,
	invalidInput: styles.parameterInvalidInput,
	labelText: styles.wideParameterLabelText,
};

const GeneralInformationTextInputStyles = {
	label: styles.generalInformationParameterLabel,
	input: styles.generalInformationParameterInput,
	invalidInput: styles.generalInformationParameterInvalidInput,
	labelText: styles.generalInformationParameterLabelText,
};

export interface Value {
	value: string;
	isValid: boolean | null;
}

export interface PriceValue {
	value: string;
	currency: string;
	isValid: boolean | null;
}

export interface Values {
	basketVersion: string;
	companyName: string;
	concentration: string;
	packSize: string;
	pharmaceuticalForm: string;
	productName: string;
	scientificName: string;
	proposedCifPrice: PriceValue;
	nationality: Value;
	exFactoryPrice: PriceValue;
	wholesalePrice: PriceValue;
	publicPrice: PriceValue;
}

interface Props {
	versions: ReferenceBasketVersion[] | null;
	values: Values;
	updateStatus: Status | null;
	onReferenceBasketVersionChange(version: string): void;
	onValueChange(fieldName: string, value: any): void;
	onPriceChange(fieldName: string, price: PriceValue): void;
	onSave(): void;
}

export default class ParametersPanel extends PureComponent<Props> {
	render() {
		const {
			values: {
				companyName,
				concentration,
				packSize,
				pharmaceuticalForm,
				productName,
				scientificName,
				proposedCifPrice,
				nationality,
				exFactoryPrice,
				wholesalePrice,
				publicPrice,
			},
			updateStatus,
			onValueChange,
			onPriceChange,
		} = this.props;

		return (
			<div className={styles.container} data-testid='parameters-panel'>
				<div className={styles.content}>
					<div className={styles.parameters}>
						<p className={styles.parameterDescription}>Maximum price calculation parameters:</p>

						{this.renderVersionSelect()}
						<SaveActivityIndicator
							hideAfterMilliseconds={3000}
							status={updateStatus}
							testId='parameters-panel-save-indicator'
						/>

						<p className={styles.parametersDescription}>General information:</p>

						<div className={styles.generalInformationContainer}>
							<div className={styles.generalInformationLeftColumn}>
								{this.renderTextInput(
									'aemaxprice-product-name',
									'Product name:',
									productName,
									true,
									(value) => onValueChange('productName', value),
									GeneralInformationTextInputStyles
								)}
								{this.renderTextInput(
									'aemaxprice-scientific-name',
									'Scientific name: (active substance)',
									scientificName,
									true,
									(value) => onValueChange('scientificName', value),
									GeneralInformationTextInputStyles
								)}
								{this.renderTextInput(
									'aemaxprice-company-name',
									'Company name:',
									companyName,
									true,
									(value) => onValueChange('companyName', value),
									GeneralInformationTextInputStyles
								)}
							</div>
							<div className={styles.generalInformationRightColumn}>
								{this.renderTextInput(
									'aemaxprice-Concentration',
									'Concentration',
									concentration,
									true,
									(value) => onValueChange('concentration', value),
									GeneralInformationTextInputStyles
								)}
								{this.renderTextInput(
									'aemaxprice-pharmaceutical-form',
									'Pharmaceutical form',
									pharmaceuticalForm,
									true,
									(value) => onValueChange('pharmaceuticalForm', value),
									GeneralInformationTextInputStyles
								)}
								{this.renderTextInput(
									'aemaxprice-pack-size',
									'Pack size',
									packSize,
									true,
									(value) => onValueChange('packSize', value),
									GeneralInformationTextInputStyles
								)}
							</div>
						</div>

						<p className={styles.parameterDescription}>Proposed CIF price to UAE:</p>
						{this.renderPriceInput('aemaxprice-proposed-cif-price', 'CIF price: *', proposedCifPrice, (value) =>
							onPriceChange('proposedCifPrice', value)
						)}

						<p className={styles.parameterDescription}>Country of origin price:</p>
						{this.renderPriceInput('aemaxprice-ex-factory-price', 'Ex-factory price: *', exFactoryPrice, (value) =>
							onPriceChange('exFactoryPrice', value)
						)}
						<label className={styles.parameterLabel} htmlFor='aemaxprice-nationality'>
							<span className={styles.parameterLabelText}>Nationality: *</span>
							<NationalityAutocomplete
								inputStyles={{
									default: styles.autocompleteInput,
									invalid: styles.autocompleteInvalidInput,
								}}
								isInvalid={!nationality.isValid}
								options={[]}
								value={nationality.value}
								onChange={(value) => onValueChange('nationality', { ...nationality, value })}
								onSelect={this.nationalitySelected}
							/>
						</label>
						{this.renderPriceInput('aemaxprice-wholesale-price', 'Wholesale price:', wholesalePrice, (value) =>
							onPriceChange('wholesalePrice', value)
						)}
						{this.renderPriceInput('aemaxprice-public-price', 'Public price:', publicPrice, (value) =>
							onPriceChange('publicPrice', value)
						)}

						<p className={styles.note}>* required items</p>
					</div>
				</div>
			</div>
		);
	}

	renderVersionSelect() {
		const {
			versions,
			values: { basketVersion },
			onReferenceBasketVersionChange,
		} = this.props;
		const currentVersion = versions && versions.find((version) => version.id === basketVersion);

		const descriptionIcon = (
			<span className={styles.parameterIcon}>
				<Icon name='violet-question-mark' />
			</span>
		);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-basket-version'>
				<span className={styles.parameterLabelText}>Reference basket valid:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={!versions}
						id='reference-basket-version'
						styles={{ select: styles.select }}
						value={basketVersion}
						onChange={(event) => onReferenceBasketVersionChange(event.target.value)}
					>
						{versions
							? versions.map((version) => (
									<option key={version.id} value={version.id}>
										Since {formatNumericHumanDate(version.sinceDate)}
									</option>
							  ))
							: null}
					</Select>

					{currentVersion && currentVersion.description !== '' ? (
						<Tooltip reference={descriptionIcon}>{currentVersion.description}</Tooltip>
					) : null}
				</div>
			</label>
		);
	}

	renderTextInput(
		id: string,
		label: string,
		value: string,
		isValid: boolean | null,
		onChange: (version: string) => void,
		styles: {
			label: string;
			input: string;
			invalidInput: string;
			labelText: string;
		} = TextInputStyles
	) {
		const { onSave } = this.props;

		return (
			<label className={styles.label} htmlFor={id}>
				<span className={styles.labelText}>{label}</span>
				<DebounceInput value={value} onBlur={onSave} onChange={onChange}>
					<Input
						id={id}
						isInvalid={isValid === false}
						styles={{ default: styles.input, invalid: styles.invalidInput }}
					/>
				</DebounceInput>
			</label>
		);
	}

	renderPriceInput(id: string, label: string, priceValue: PriceValue, onChange: (value: PriceValue) => void) {
		const inputStyles = {
			label: styles.priceParameterLabel,
			input: styles.priceParameterInput,
			invalidInput: styles.priceParameterInvalidInput,
			labelText: styles.parameterLabelText,
		};

		return (
			<Fragment>
				{this.renderTextInput(
					id,
					label,
					priceValue.value,
					priceValue.isValid,
					(value) =>
						onChange({
							value,
							currency: priceValue.currency,
							isValid: priceValue.isValid,
						}),
					inputStyles
				)}
				<Select
					id={`${id}-currency`}
					styles={{ select: styles.currencySelect }}
					value={priceValue.currency}
					onChange={(event) =>
						onChange({
							value: priceValue.value,
							currency: event.target.value,
							isValid: priceValue.isValid,
						})
					}
				>
					<option value='USD'>USD</option>
					<option value='AED'>AED</option>
				</Select>
			</Fragment>
		);
	}

	nationalitySelected = (options: { id: string; title: string }[]) => {
		const {
			values: { nationality },
			onValueChange,
		} = this.props;
		if (options.length === 0) {
			return;
		}
		onValueChange('nationality', { ...nationality, value: options[0].title });
	};
}
